import { useEffect, useRef } from "react";
import {
  Alert,
  Box,
  Button,
  List,
  ListItem,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useAuth, useHomeFeed } from "@bw/shared/hooks";
import { guardianDenialFromError } from "@bw/shared/api";
import type {
  RankedMatchActivityCard,
} from "@bw/shared/types/generated";
import { Link as RouterLink } from "react-router-dom";
import { Page } from "../../../common/components/Page";
import { useGuardianLookOnly } from "../../../common/query/useGuardianLink";
import {
  FeedSkeleton,
  PostActions,
  PostCard,
  PostComposer,
  PostMenu,
} from "../../social";
import type { HomePageProps } from "../types";
import { HomeOffers } from "../components/HomeOffers";

const HOME_FEED_PAGE_SIZE = 10;
const HOME_AUTOLOAD_PAGE_LIMIT = 10;
const HOME_AUTOLOAD_ITEM_LIMIT = 100;

/**
 * Home composes the app-injected personal agenda before the network feed.
 * The page intentionally has no page-title hero:
 * the active Home destination is already identified by the application shell.
 * Personal reads remain auth-gated and every displayed fact comes from the same
 * server-backed hooks as the previous presentation.
 */
export function HomePage({ agenda }: HomePageProps) {
  const { user } = useAuth();
  const signedIn = Boolean(user);
  // Home stays browsable in look-only mode (#2691): a minor with no guardian
  // yet, or one a guardian paused. The app shell's banner says why and links to
  // the guardian request; Home just keeps the network feed closed meanwhile.
  const { lookOnly, paused, known } = useGuardianLookOnly(signedIn);
  const feedOpen = signedIn && known && !lookOnly;
  const feed = useHomeFeed(
    { limit: HOME_FEED_PAGE_SIZE },
    {
      enabled: feedOpen,
      freshness: { enabled: feedOpen, intervalMs: 30_000 },
    },
  );


  return (
    <Page>
      <Stack spacing={{ xs: 2.5, md: 3 }} sx={{ pb: 4 }}>
        <Typography
          component="h1"
          sx={{
            position: "absolute",
            // Numeric sizing values are proportional in MUI (`width: 1` is
            // 100%), which would make this accessible-only heading overflow
            // the content column. Keep the clipping box explicitly one pixel.
            width: "1px",
            height: "1px",
            p: 0,
            m: "-1px",
            overflow: "hidden",
            clip: "rect(0 0 0 0)",
            whiteSpace: "nowrap",
            border: 0,
          }}
        >
          Badminton Warriors home
        </Typography>
        {signedIn ? agenda : null}
        <HomeOffers />

        {signedIn && ((known && lookOnly) || guardianDenialFromError(feed.error)) ? (
          <LookOnlyFeed paused={paused} />
        ) : (
          <FeedCenterpiece signedIn={signedIn} feed={feed} pending={signedIn && !known} />
        )}
      </Stack>
    </Page>
  );
}

function LookOnlyFeed({ paused }: { paused: boolean }) {
  return (
    <Box component="section" aria-labelledby="home-feed-heading">
      <Stack spacing={1}>
        <Typography id="home-feed-heading" component="h2" sx={{ fontSize: "1rem", fontWeight: 700, px: { xs: 0.5, sm: 0 } }}>
          From your network
        </Typography>
        <Paper variant="outlined" sx={{ p: 2.5 }}>
          <Typography color="text.secondary">
            {paused
              ? "Posts from friends and communities come back when a guardian turns your account back on."
              : "Posts from friends and communities appear once a parent or guardian approves your account."}
          </Typography>
        </Paper>
      </Stack>
    </Box>
  );
}

function FeedCenterpiece({
  signedIn,
  feed,
  pending = false,
}: {
  signedIn: boolean;
  feed: ReturnType<typeof useHomeFeed>;
  /** The account's look-only state is still loading; the feed waits for it. */
  pending?: boolean;
}) {
  const items = feed.items;
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const pagesLoaded = feed.data?.pages.length ?? 0;
  const autoLoad =
    pagesLoaded < HOME_AUTOLOAD_PAGE_LIMIT &&
    items.length < HOME_AUTOLOAD_ITEM_LIMIT;
  useEffect(() => {
    const node = sentinelRef.current;
    if (
      !node ||
      !autoLoad ||
      !feed.hasNextPage ||
      feed.isFetchingNextPage ||
      feed.isFetchNextPageError ||
      typeof IntersectionObserver === "undefined"
    )
      return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          void feed.fetchNextPage();
        }
      },
      { rootMargin: "320px 0px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [
    autoLoad,
    feed.fetchNextPage,
    feed.hasNextPage,
    feed.isFetchNextPageError,
    feed.isFetchingNextPage,
    pagesLoaded,
  ]);
  return (
    <Box component="section" aria-labelledby="home-feed-heading">
      <Stack spacing={0.5}>
        <Stack
          direction="row"
          spacing={1.5}
          useFlexGap
          sx={{ alignItems: "center", justifyContent: "space-between" }}
        >
          <Typography id="home-feed-heading" component="h2" sx={{ fontSize: "1rem", fontWeight: 700, px: { xs: 0.5, sm: 0 } }}>
            From your network
          </Typography>
          {signedIn ? <PostComposer trigger="icon" /> : null}
        </Stack>

        {signedIn && feed.freshnessError ? (
          <Alert
            severity="warning"
            role="status"
            action={
              <Button
                color="inherit"
                size="small"
                disabled={feed.isCheckingFreshness}
                onClick={() => void feed.checkFreshness()}
              >
                Try again
              </Button>
            }
          >
            Feed freshness is temporarily unavailable; loaded activity remains.
          </Alert>
        ) : signedIn && feed.newPostsAvailable ? (
          <Alert
            severity="info"
            role="status"
            action={
              <Button
                color="inherit"
                size="small"
                disabled={feed.isApplyingFreshness || feed.isCheckingFreshness}
                onClick={() => void feed.refreshNewPosts()}
              >
                {feed.isApplyingFreshness ? "Updating…" : "Show new activity"}
              </Button>
            }
          >
            New network activity is available.
          </Alert>
        ) : null}

        {signedIn && feed.partialSources.includes("activity") ? (
          <Alert
            severity="warning"
            role="status"
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => feed.refetch()}
              >
                Try again
              </Button>
            }
          >
            Match activity is temporarily unavailable; posts are still current.
          </Alert>
        ) : null}

        {!signedIn ? (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={1.5} sx={{ alignItems: "flex-start" }}>
              <Typography color="text.secondary">
                Sign in to see posts from friends, players, and communities you
                follow.
              </Typography>
              <Button component={RouterLink} to="/login" size="small">
                Sign in
              </Button>
            </Stack>
          </Paper>
        ) : feed.isError && !feed.isFetchNextPageError ? (
          <Alert
            severity="error"
            role="alert"
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => feed.refetch()}
              >
                Try again
              </Button>
            }
          >
            We couldn&apos;t load your feed.
          </Alert>
        ) : feed.isLoading || pending ? (
          <FeedSkeleton />
        ) : items.length === 0 && !feed.hasNextPage ? (
          <Paper variant="outlined" sx={{ p: 2.5 }}>
            <Typography color="text.secondary">
              No network activity yet. Add friends or share a post to build your
              Home feed.
            </Typography>
          </Paper>
        ) : (
          <Stack spacing={1.5}>
            <List disablePadding sx={{ display: "grid", gap: 2 }}>
              {items.map((item) =>
                item.kind === "post" ? (
                  <ListItem key={`post:${item.post.post_id}`} disablePadding>
                    <PostCard
                      post={item.post}
                      actions={<PostActions post={item.post} />}
                      menu={<PostMenu post={item.post} />}
                      detailPath={(id) => `/community/posts/${id}`}
                      profilePath={(uid) => `/players/${uid}`}
                    />
                  </ListItem>
                ) : (
                  <ListItem
                    key={`activity:${item.activity.activity_id}`}
                    disablePadding
                  >
                    <RankedMatchActivityCardView activity={item.activity} />
                  </ListItem>
                ),
              )}
            </List>
            {feed.isFetchNextPageError ? (
              <Alert
                severity="error"
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => void feed.fetchNextPage()}
                  >
                    Retry
                  </Button>
                }
              >
                We couldn&apos;t load more activity.
              </Alert>
            ) : feed.hasNextPage && autoLoad ? (
              <Box ref={sentinelRef} sx={{ minHeight: 1 }}>
                {feed.isFetchingNextPage ? (
                  <Typography role="status" color="text.secondary">
                    Loading more activity…
                  </Typography>
                ) : null}
              </Box>
            ) : feed.hasNextPage ? (
              <Button
                onClick={() => void feed.fetchNextPage()}
                disabled={feed.isFetchingNextPage}
                size="small"
                variant="outlined"
                sx={{ alignSelf: "flex-start" }}
              >
                {feed.isFetchingNextPage ? "Loading…" : "Load more"}
              </Button>
            ) : null}
          </Stack>
        )}
      </Stack>
    </Box>
  );
}

function RankedMatchActivityCardView({
  activity,
}: {
  activity: RankedMatchActivityCard;
}) {
  const resultCopy =
    activity.outcome === "won" ? "won a ranked" : "lost a ranked";
  return (
    <Paper
      component="article"
      aria-label={`${activity.actor.display_name} ${resultCopy} ${activity.mode} match`}
      variant="outlined"
      sx={{ width: "100%", minWidth: 0, p: 2 }}
    >
      <Stack spacing={1} sx={{ minWidth: 0 }}>
        <Typography
          component="h3"
          variant="subtitle1"
          sx={{ fontWeight: 700, overflowWrap: "anywhere" }}
        >
          {activity.actor.display_name} {resultCopy} {activity.mode} match.
        </Typography>
        <Typography color="text.secondary" variant="caption">
          <time dateTime={activity.occurred_at}>
            {new Intl.DateTimeFormat("en-US", {
              dateStyle: "medium",
              timeStyle: "short",
            }).format(new Date(activity.occurred_at))}
          </time>
        </Typography>
        <Button
          component={RouterLink}
          to={`/players/${encodeURIComponent(activity.action.user_id)}`}
          size="small"
          sx={{
            alignSelf: "flex-start",
            maxWidth: "100%",
            overflowWrap: "anywhere",
            textAlign: "left",
          }}
        >
          View {activity.actor.display_name}&apos;s profile
        </Button>
      </Stack>
    </Paper>
  );
}
